import { AsyncPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core'
import { MatFormField } from '@angular/material/form-field'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import {
  ElderBasicPaneLayoutComponent,
  ElderDataToolbarComponent,
  ElderPaneComponent,
  ElderPaneSubtitleComponent,
  ElderSelectComponent,
  ElderTableActivationDirective,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderToolbarContentDirective,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject } from 'rxjs'
import { DynamicPropertyConf } from '../dynamic-property-conf'
import { DynamicPropertyConfDetailComponent } from '../dynamic-property-conf-detail/dynamic-property-conf-detail.component'
import { DynamicPropertyMaterializer } from '../dynamic-property-materializer'
import { DynamicPropertySpec } from '../dynamic-property-spec'

@Component({
  selector: 'app-dynamic-property-conf-browser',
  templateUrl: './dynamic-property-conf-browser.component.html',
  styleUrls: ['./dynamic-property-conf-browser.component.scss'],
  imports: [
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
    ElderPaneSubtitleComponent,
    ElderTableComponent,
    ElderTableActivationDirective,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    ElderToolbarContentDirective,
    MatFormField,
    ElderSelectComponent,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    DynamicPropertyConfDetailComponent,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class DynamicPropertyConfBrowserComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  public readonly dynamicPropertyConfs$ = new BehaviorSubject<DynamicPropertyConf[]>([])
  public readonly selectedConf$ = new BehaviorSubject<DynamicPropertyConf>(null)

  public readonly _availableSpecs$ = new BehaviorSubject<DynamicPropertySpec[]>([])

  public dummyValue: DynamicPropertySpec

  @Output()
  public readonly dynamicPropertyConfsChange: EventEmitter<DynamicPropertyConf[]> =
    new EventEmitter<DynamicPropertyConf[]>()

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {}

  /***************************************************************************
   *                                                                         *
   * Getter / Setters                                                        *
   *                                                                         *
   **************************************************************************/

  public get selectedConf(): DynamicPropertyConf {
    return this.selectedConf$.getValue()
  }

  public set selectedConf(conf: DynamicPropertyConf) {
    this.selectedConf$.next(conf)
  }

  @Input()
  public set dynamicPropertyConfs(confs: DynamicPropertyConf[]) {
    this.sortConfs(confs)
    this.dynamicPropertyConfs$.next(confs)
    this.autoSelectConf()
  }

  public get dynamicPropertyConfs() {
    return this.dynamicPropertyConfs$.getValue()
  }

  @Input()
  public set availableSpecs(specs: DynamicPropertySpec[]) {
    this._availableSpecs$.next(specs)
  }

  public get availableSkillSetSpecs() {
    return this._availableSpecs$.getValue()
  }

  /***************************************************************************
   *                                                                         *
   * Component Events                                                        *
   *                                                                         *
   **************************************************************************/

  public onNewSkillSet(spec: DynamicPropertySpec): void {
    if (spec) {
      // Only needed because of the dummy value reset
      const confs = [...this.dynamicPropertyConfs]
      const skillSetConf = DynamicPropertyMaterializer.start(spec).description('Neu').materialize()

      confs.push(skillSetConf)
      this.dynamicPropertyConfs = confs
      this.selectedConf$.next(skillSetConf)

      this.dummyValue = spec
      setTimeout(() => {
        this.dummyValue = null
      })

      this.dynamicPropertyConfsChange.emit(this.dynamicPropertyConfs)
    }
  }

  public onRemoveSkillSets(toRemove: DynamicPropertyConf[]): void {
    if (toRemove.length > 0) {
      const skillSets = [...this.dynamicPropertyConfs]
      toRemove.forEach((c) => skillSets.splice(skillSets.indexOf(c), 1))
      this.dynamicPropertyConfs = skillSets
      this.selectedConf$.next(null)
      this.dynamicPropertyConfsChange.emit(this.dynamicPropertyConfs)
    } else {
      this.log.info('No confs provided to delete!')
    }
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private autoSelectConf(): void {
    const confs = this.dynamicPropertyConfs
    if (confs && confs.length > 0) {
      const autoSelected = this.dynamicPropertyConfs.find((ss) => ss.id === this.selectedConf?.id)
      this.selectedConf = autoSelected ? autoSelected : confs[0]
    } else {
      this.selectedConf = null
    }
  }

  private sortConfs(confs: DynamicPropertyConf[]): void {
    if (confs) {
      confs.sort(
        (sa, sb) => sa?.description?.localeCompare(sb?.description) || sa.id.localeCompare(sb.id) // Alphabetically by conf description // then by conf id
      )
    }
  }
}
